import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import { Person } from "@mui/icons-material";
import {
  GenerationalChain,
  GenerationalArtOrder,
  getChainDescriptionForOrder,
  checkChainCompleteness,
} from "../../utils/generationalArtUtils";

interface ChainPreviewPanelProps {
  selectedChain: GenerationalChain;
  order: GenerationalArtOrder;
  isCompleted?: boolean;
}

const ChainPreviewPanel: React.FC<ChainPreviewPanelProps> = ({
  selectedChain,
  order,
  isCompleted = false,
}) => {
  const completeness = checkChainCompleteness(
    selectedChain,
    order.maxGenerations
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Selected Chain Preview
        </Typography>

        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {getChainDescriptionForOrder(selectedChain, order.maxGenerations)}
        </Typography>

        {!isCompleted && !completeness.isComplete && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ Incomplete Chain:</strong> Some family members are
              missing information:
              <br />• Missing photos: {completeness.missingPhotos.length}
              <br />• Missing names: {completeness.missingNames.length}
              <br />• Missing birth dates:{" "}
              {completeness.missingBirthdates.length}
            </Typography>
          </Alert>
        )}

        <Paper
          variant="outlined"
          sx={{ p: 0.5, maxHeight: 400, overflow: "auto" }}
        >
          <List dense>
            {selectedChain.members
              .slice(0, order.maxGenerations)
              .map((member, index) => (
                <React.Fragment key={member.id}>
                  <ListItem sx={{ py: 0.5, px: 1 }}>
                    <ListItemAvatar sx={{ mr: 2 }}>
                      {member.imageMetadata &&
                      !member.imageMetadata.includes("Placeholder") ? (
                        <Avatar
                          src={member.imageMetadata}
                          sx={{
                            width: 56,
                            height: 56,
                            border: "2px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Person />
                        </Avatar>
                      ) : (
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            border: "2px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Person />
                        </Avatar>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {member.name || "Unknown"}
                          </Typography>
                          <Chip
                            label={`Gen ${index + 1}`}
                            size="small"
                            variant="outlined"
                          />
                          {!isCompleted && (
                            <>
                              {(!member.imageMetadata ||
                                member.imageMetadata.includes(
                                  "Placeholder"
                                )) && (
                                <Chip
                                  label="No Photo"
                                  size="small"
                                  color="warning"
                                />
                              )}
                              {(!member.name || member.name.trim() === "") && (
                                <Chip
                                  label="No Name"
                                  size="small"
                                  color="error"
                                />
                              )}
                              {(!member.birthdate ||
                                member.birthdate.trim() === "") && (
                                <Chip
                                  label="No Birth Date"
                                  size="small"
                                  color="error"
                                />
                              )}
                            </>
                          )}
                        </Box>
                      }
                      secondary={
                        member.birthdate
                          ? `Born: ${member.birthdate}${
                              member.deathdate
                                ? ` - Died: ${member.deathdate}`
                                : ""
                            }`
                          : "Unknown birthdate"
                      }
                    />
                  </ListItem>
                  {index <
                    Math.min(
                      selectedChain.members.length,
                      order.maxGenerations
                    ) -
                      1 && <Divider />}
                </React.Fragment>
              ))}
          </List>
        </Paper>

        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            Total generations:{" "}
            {Math.min(selectedChain.generationCount, order.maxGenerations)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Members with photos:{" "}
            {selectedChain.members
              ?.slice(0, order.maxGenerations)
              ?.filter(
                (m) =>
                  m.imageMetadata && !m.imageMetadata.includes("Placeholder")
              )?.length || 0}
          </Typography>
          {!isCompleted && (
            <>
              {!completeness.isComplete ? (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  ⚠️ Chain completeness: {completeness.incompleteMembers.length}{" "}
                  member(s) need attention
                </Typography>
              ) : (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  ✅ Chain is complete and ready for submission
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChainPreviewPanel;
